import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EmbeddedViewRef,
  AfterViewInit,
  HostListener,
  NgZone,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { IconComponent } from '@shared/components/ui/icon/icon.component';
import { ContextMenuService } from './context-menu.service';
import {
  ContextMenuConfig,
  ContextMenuItem,
  ContextMenuPlacement,
  ContextMenuSelectEvent,
  ContextMenuItemVariant,
  ContextMenuStyleConfig,
  ContextMenuTriggerConfig,
} from './context-menu.model';

/** An item after the per-row predicates have been resolved against the bound `context`. */
interface ResolvedEntry<T> {
  item: ContextMenuItem<T>;
  label: string;
  /** `hintWhen(context)` when present, else the static `hint`. */
  hint: string;
  /** `iconWhen`/`variantWhen` resolved, so the template reads one value rather than a fallback chain. */
  icon: string;
  variant: ContextMenuItemVariant;
  disabled: boolean;
  /** Divider above this entry (already suppressed when it would be the first thing in the panel). */
  separator: boolean;
  /** Section heading above this entry (already de-duplicated against the previous entry). */
  section: string | null;
}

const DEFAULT_TRIGGER: Required<
  Pick<ContextMenuTriggerConfig, 'icon' | 'iconSize' | 'tooltip' | 'variant' | 'size'>
> = {
  icon: 'more',
  iconSize: 15,
  tooltip: 'More actions',
  variant: 'icon',
  size: 'md',
};

const VIEWPORT_MARGIN = 8;

/** Content mode: clicks on a real control inside the content belong to it, not to the panel. */
const DEFAULT_CONTENT_IGNORE = 'button, a, input, select, textarea, label, [contenteditable]';

/** Hover mode: rest before opening, and grace before closing (long enough to cross onto the panel). */
const HOVER_OPEN_DELAY = 80;
const HOVER_CLOSE_DELAY = 160;

/** A menu taller than this many rows scrolls instead of growing. */
/**
 * Below this a clamped menu shows about two and a half rows, which reads as broken; overhanging the
 * viewport edge by a few pixels reads as a menu.
 */
const MIN_CLAMP_HEIGHT = 180;

/** Is the event target a field where the scroll keys mean typing / caret movement? */
function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  );
}

/** Keys that scroll a page — blocked outside the panel while the menu is open. */
const SCROLL_KEYS = new Set([
  'PageUp',
  'PageDown',
  'Home',
  'End',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  ' ',
  'Spacebar',
]);

/**
 * The common kebab / "More actions" context menu — one config-driven component for every row, card and
 * detail-header action menu.
 *
 * The panel is created in this component's view (so bindings and encapsulated styles keep working) and then
 * moved to `document.body` and positioned `fixed` from the trigger's rect. That is deliberate: a menu left
 * inside a grid cell or a card with `overflow: hidden` / `isolation: isolate` gets clipped or painted under
 * its siblings. Features must NOT hand-roll that trick again — use this component.
 *
 * ```html
 * <app-context-menu [config]="taskMenu" [context]="task" (itemSelect)="onMenuSelect($event)" />
 * ```
 *
 * For a right-click menu (or a trigger the feature owns), hide the built-in button with
 * `[showTrigger]="false"` and call `openAtEvent(event)` / `openFor(element)` on the component ref.
 */
@Component({
  selector: 'app-context-menu',
  standalone: true,
  imports: [IconComponent, NgbTooltipModule, NgTemplateOutlet],
  template: `
    @let trg = trigger();
    @if (showTrigger()) {
      <button
        #triggerBtn
        type="button"
        class="cm-trigger"
        [class]="
          'cm-trigger--' + trg.variant + ' cm-trigger--' + trg.size + ' ' + (trg.cssClass ?? '')
        "
        [class.is-open]="isOpen()"
        [disabled]="isTriggerDisabled()"
        [ngbTooltip]="trg.tooltip"
        [disableTooltip]="!trg.tooltip"
        container="body"
        aria-haspopup="menu"
        [attr.aria-expanded]="isOpen()"
        [attr.aria-label]="trg.ariaLabel ?? trg.label ?? trg.tooltip ?? 'More actions'"
        (click)="onTriggerClick($event)"
        (mouseenter)="onHoverIn(triggerBtn)"
        (mouseleave)="onHoverOut()"
        (focus)="onHoverIn(triggerBtn)"
        (blur)="onHoverOut()"
      >
        @if (trg.icon) {
          <app-icon [name]="trg.icon" [size]="trg.iconSize ?? 15" />
        }
        @if (trg.label) {
          <span class="cm-trigger-label">{{ trg.label }}</span>
        }
        @if (trg.showCaret) {
          <app-icon name="chevronDown" [size]="12" class="cm-trigger-caret" />
        }
      </button>
    }

    <!-- Projected content. With trigger.openFromContent it IS the trigger (see onHostClick); otherwise it
         simply renders alongside the built-in button. -->
    <ng-content></ng-content>

    <ng-template #panelTpl>
      @let cfg = config();
      @let st = style();
      @let custom = panelContent();
      <div
        class="cm-panel"
        [class]="st.panelClass ?? ''"
        [class.cm-panel--animate]="st.animate !== false"
        [class.cm-panel--compact]="st.density === 'compact'"
        [class.cm-panel--aligned]="st.alignLabels !== false"
        [class.cm-panel--bare]="!!custom"
        [style]="panelVars()"
        [attr.role]="custom ? null : 'menu'"
        tabindex="-1"
        [attr.aria-label]="cfg.ariaLabel ?? 'Actions'"
        (click)="$event.stopPropagation()"
        (keydown)="onKeydown($event)"
        (mouseenter)="onPanelHoverIn()"
        (mouseleave)="onHoverOut()"
      >
        @if (custom && st.closable) {
          <!-- Chrome, not content: the feature's own template is untouched, and this sits over its
               corner. Escape and an outside click still work; this is the version you can see. -->
          <button
            type="button"
            class="cm-close"
            ngbTooltip="Close"
            aria-label="Close"
            (click)="close()"
          >
            <app-icon name="x" [size]="14" />
          </button>
        }
        @if (custom) {
          <!-- The feature owns the whole surface: it gets the bound context as $implicit, and the panel
               drops its own chrome (see cm-panel--bare) so there is no card-inside-a-card. -->
          <ng-container
            [ngTemplateOutlet]="custom"
            [ngTemplateOutletContext]="{ $implicit: context(), context: context() }"
          />
        }
        @if (!custom && cfg.header) {
          <div class="cm-header">{{ cfg.header }}</div>
        }

        @for (entry of custom ? [] : entries(); track entry.item.id) {
          @if (entry.separator) {
            <div class="cm-sep"></div>
          }
          @if (entry.section) {
            <div class="cm-section">{{ entry.section }}</div>
          }
          <ng-template #itemBody>
            @if (entry.icon) {
              <app-icon class="cm-item-icon" [name]="entry.icon" [size]="15" />
            } @else if (st.alignLabels !== false && hasIcons()) {
              <span class="cm-item-icon"></span>
            }
            @if (entry.hint && entry.item.hintPlacement === 'below') {
              <!-- A reason is a sentence, and a sentence needs the row to itself. -->
              <span class="cm-item-text">
                <span class="cm-item-label">{{ entry.label }}</span>
                <span class="cm-item-hint is-below">{{ entry.hint }}</span>
              </span>
            } @else {
              <span class="cm-item-label">{{ entry.label }}</span>
              @if (entry.hint) {
                <span class="cm-item-hint">{{ entry.hint }}</span>
              }
            }
            @if (entry.item.checked) {
              <app-icon class="cm-item-check" name="check" [size]="14" />
            }
          </ng-template>

          <!-- A read-only row is a plain div, not a disabled button: disabled would dim it, and a
               menuitem the user cannot activate shouldn't be a focus stop at all. -->
          @if (entry.item.readonly) {
            <div
              class="cm-item is-readonly"
              [class]="'cm-item--' + entry.variant + ' ' + (st.itemClass ?? '')"
              [class.is-band]="entry.item.emphasis === 'band'"
              [attr.data-variant]="entry.variant"
              [ngbTooltip]="entry.item.tooltip"
              [disableTooltip]="!entry.item.tooltip"
              container="body"
            >
              <ng-container [ngTemplateOutlet]="itemBody" />
            </div>
          } @else {
            <button
              type="button"
              role="menuitem"
              class="cm-item"
              [class]="'cm-item--' + entry.variant + ' ' + (st.itemClass ?? '')"
              [class.is-disabled]="entry.disabled"
              [class.is-band]="entry.item.emphasis === 'band'"
              [attr.data-variant]="entry.variant"
              [attr.aria-disabled]="entry.disabled"
              [ngbTooltip]="entry.item.tooltip"
              [disableTooltip]="!entry.item.tooltip"
              container="body"
              (click)="select(entry)"
            >
              <ng-container [ngTemplateOutlet]="itemBody" />
            </button>
          }
        }

        @if (!custom && !entries().length && cfg.emptyText) {
          <div class="cm-empty">{{ cfg.emptyText }}</div>
        }
      </div>
    </ng-template>
  `,
  styles: [
    `
      :host {
        display: inline-flex;
        position: relative;
      }

      /* ── trigger ─────────────────────────────────────── */
      .cm-trigger {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        border: 1px solid transparent;
        background: transparent;
        border-radius: var(--radius-sm, 0.375rem);
        /* --cm-trigger-color lets a host tint the kebab: a custom property crosses the encapsulation
           boundary, which a plain color on the host cannot. Needed wherever the trigger sits on a
           coloured surface (e.g. a selected row), where the default grey has no contrast. */
        color: var(--cm-trigger-color, var(--fg-4, #9ca3af));
        font: inherit;
        font-size: var(--text-sm, 0.75rem);
        font-weight: var(--fw-medium, 500);
        cursor: pointer;
        transition:
          background var(--motion-fast, 0.15s ease),
          color var(--motion-fast, 0.15s ease),
          border-color var(--motion-fast, 0.15s ease);

        &:disabled {
          opacity: 0.5;
          cursor: default;
        }

        &:focus-visible {
          outline: 2px solid var(--h4c-bright-blue, #27aae1);
          outline-offset: 1px;
        }
      }

      .cm-trigger--icon {
        padding: 0;

        /* md matches the app's standard 30px row icon-button. */
        &.cm-trigger--sm {
          width: 26px;
          height: 26px;
        }
        &.cm-trigger--md {
          width: 30px;
          height: 30px;
        }
        &.cm-trigger--lg {
          width: 34px;
          height: 34px;
        }
      }

      .cm-trigger--ghost,
      .cm-trigger--outline,
      .cm-trigger--solid {
        color: var(--fg-2, #4e5156);

        &.cm-trigger--sm {
          padding: 4px 8px;
        }
        &.cm-trigger--md {
          padding: 6px 12px;
        }
        &.cm-trigger--lg {
          padding: 9px 16px;
        }
      }

      .cm-trigger--outline {
        border-color: var(--border-default, #e5e7eb);
        background: var(--bg-card, #fff);
      }

      .cm-trigger--solid {
        background: var(--h4c-dark-blue, #262262);
        border-color: var(--h4c-dark-blue, #262262);
        color: #fff;
      }

      .cm-trigger--icon:hover:not(:disabled),
      .cm-trigger--ghost:hover:not(:disabled) {
        background: var(--bg-subtle, #f3f4f6);
        color: var(--h4c-dark-blue, #262262);
      }

      .cm-trigger--outline:hover:not(:disabled) {
        border-color: var(--h4c-bright-blue, #27aae1);
        color: var(--h4c-dark-blue, #262262);
      }

      .cm-trigger--solid:hover:not(:disabled) {
        opacity: 0.9;
      }

      .cm-trigger--icon.is-open,
      .cm-trigger--ghost.is-open {
        background: var(--bg-subtle, #f3f4f6);
        color: var(--h4c-dark-blue, #262262);
      }

      .cm-trigger-caret {
        opacity: 0.7;
      }

      /* ── panel (lives in document.body, positioned from the trigger) ─── */
      .cm-panel {
        position: fixed;
        top: 0;
        left: -9999px;
        z-index: var(--cm-z, 1000);
        width: var(--cm-width, 180px);
        min-width: var(--cm-min-width, auto);
        /* Only set inline when the content genuinely cannot fit the viewport — see applyFit(). This is
           the value used for the instant between mount and that measurement, and when a config pins
           maxHeight. */
        max-height: var(--cm-max-height, none);
        /*
         * BOTH axes, explicitly. overflow-y: auto on its own is not "vertical only": CSS promotes a
         * visible axis to auto when the other axis is not visible, so declaring the y axis alone gave
         * the x axis auto too — and that is the horizontal scrollbar. clip also refuses programmatic
         * scrolling, which hidden allows; the hidden line below is the fallback for engines without it.
         */
        overflow-x: hidden;
        overflow-x: clip;
        overflow-y: auto;
        /* Reserve the scrollbar lane so engaging overflow-y does not narrow the content box and reflow
           every label inside it. */
        scrollbar-gutter: stable;
        /* Explicit, so a global transition: all can never animate top / left / width / max-height —
           a position that animates is a position that measures wrong while it is in flight. */
        transition:
          opacity 0.12s ease,
          transform 0.12s ease;
        padding: 4px;
        display: flex;
        flex-direction: column;
        background: var(--cm-bg, var(--bg-card, #fff));
        border: 1px solid var(--cm-border, var(--border-default, #e5e7eb));
        border-radius: var(--cm-radius, var(--radius-md, 0.5rem));
        box-shadow: var(--cm-shadow, var(--shadow-lg, 0 10px 30px rgba(15, 23, 42, 0.14)));

        &:focus {
          outline: none;
        }

        /* A 6px rail, for the one case that earns it. The platform default is ~15px, which inside a
           262px menu is 6% of the panel spent on a control nobody asked for. */
        scrollbar-width: thin;
        scrollbar-color: #d6dce6 transparent;

        &::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }

        &::-webkit-scrollbar-track {
          background: transparent;
        }

        &::-webkit-scrollbar-thumb {
          background: #d6dce6;
          border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb:hover {
          background: #c2cad8;
        }
      }

      /* panelContent mode: the projected template is the whole surface, so drop this panel's own chrome —
         otherwise the feature's card sits inside a second card, with two borders and two shadows. */
      .cm-panel--bare {
        width: auto;
        min-width: 0;
        max-height: none;
        padding: 0;
        background: transparent;
        border: 0;
        border-radius: 0;
        box-shadow: none;
        overflow: visible;
      }

      /* Over the projected card, not in its layout — the feature never asked for a header row. */
      .cm-close {
        position: absolute;
        top: 10px;
        right: 10px;
        z-index: 1;
        display: grid;
        place-items: center;
        width: 24px;
        height: 24px;
        padding: 0;
        border: 0;
        border-radius: 6px;
        background: var(--bg-card, #fff);
        color: var(--fg-3, #94a3b8);
        cursor: pointer;
      }

      .cm-close:hover {
        background: var(--bg-subtle, #f3f4f6);
        color: var(--h4c-dark-blue, #262262);
      }

      .cm-close:focus-visible {
        outline: 2px solid var(--h4c-bright-blue, #27aae1);
        outline-offset: 1px;
      }

      /* The bare panel is the positioning context the close button pins to. */
      .cm-panel--bare {
        position: fixed;
      }

      .cm-panel--animate {
        animation: cm-fade var(--motion-normal, 0.18s) ease;
      }

      @keyframes cm-fade {
        from {
          opacity: 0;
          transform: translateY(-3px);
        }
        to {
          opacity: 1;
          transform: none;
        }
      }

      .cm-header {
        padding: 7px 10px 5px;
        font-size: var(--text-xs, 0.6875rem);
        font-weight: var(--fw-semibold, 600);
        letter-spacing: 0.03em;
        text-transform: uppercase;
        color: var(--fg-4, #9ca3af);
      }

      .cm-section {
        padding: 6px 10px 3px;
        font-size: var(--text-xs, 0.6875rem);
        font-weight: var(--fw-semibold, 600);
        color: var(--fg-4, #9ca3af);
      }

      .cm-sep {
        height: 1px;
        margin: 4px 6px;
        background: var(--cm-border, var(--border-default, #e5e7eb));
      }

      .cm-item {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        border: 0;
        background: transparent;
        text-align: left;
        padding: 8px 10px;
        border-radius: var(--radius-sm, 0.375rem);
        font: inherit;
        font-size: var(--cm-font-size, var(--text-sm, 0.75rem));
        color: var(--cm-fg, var(--fg-2, #4e5156));
        cursor: pointer;
        transition: background var(--motion-fast, 0.15s ease);
        /* Each variant re-points this one property below, so the hover tint follows the item's intent
           without any selector fighting the rule underneath. */
        --cm-item-hover: var(--cm-hover-bg, var(--bg-subtle, #f3f4f6));

        &:hover:not(.is-disabled),
        &:focus-visible:not(.is-disabled) {
          background: var(--cm-item-hover);
        }

        &:focus-visible {
          outline: none;
        }

        &.is-disabled {
          opacity: 0.45;
          cursor: default;
        }

        /* A spec row, not an action: no hover tint, no pointer, and it keeps full contrast (unlike
           is-disabled, which dims — a value the user is meant to READ must not look unavailable). */
        &.is-readonly {
          cursor: default;
          background: transparent;
        }
      }

      .cm-panel--compact .cm-item {
        padding: 6px 8px;
      }

      .cm-item--danger {
        color: var(--cm-danger, var(--color-danger, #ef4444));
        --cm-item-hover: var(--cm-hover-danger, var(--color-light-danger, rgba(239, 68, 68, 0.2)));
      }
      .cm-item--success {
        color: var(--color-success, #2bb673);
        --cm-item-hover: var(
          --cm-hover-success,
          var(--color-light-success, rgba(43, 182, 115, 0.2))
        );
      }
      .cm-item--warning {
        color: var(--color-warning, #f59e0b);
        --cm-item-hover: var(
          --cm-hover-warning,
          var(--color-light-warning, rgba(245, 159, 11, 0.2))
        );
      }
      .cm-item--primary {
        color: var(--h4c-bright-blue, #27aae1);
        --cm-item-hover: var(
          --cm-hover-primary,
          var(--h4c-bright-blue-14, rgba(39, 170, 225, 0.14))
        );
      }

      .cm-item-icon {
        flex: 0 0 15px;
        width: 15px;
        color: inherit;
        opacity: 0.85;
      }

      /*
       * min-width: 0 is what makes the ellipsis actually work in a flex row.
       *
       * A flex item's default min-width: auto refuses to shrink below its MIN-CONTENT width, so a long
       * label ("opens the leave request for this day") pushed the row past the 252px content box even
       * with overflow:hidden set on it. That was the 8px of phantom scrollWidth: nothing was visibly
       * overflowing because the text was clipped — the row was simply wider than the box it sat in.
       */
      .cm-item-label {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .cm-item-hint {
        flex: 0 0 auto;
        font-size: var(--text-xs, 0.6875rem);
        color: var(--fg-4, #9ca3af);
      }

      .cm-item-text {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 1px;
      }

      .cm-item-text .cm-item-label {
        flex: none;
      }

      .cm-item-hint.is-below {
        font-size: 0.6563rem;
        line-height: 1.35;
        white-space: normal;
      }

      /*
       * The band. One item lifted out of the list, tinted by its variant — the wash says "this is the
       * one", and the label still carries the words, so nothing depends on the colour alone.
       */
      .cm-item.is-band {
        margin: 2px 4px 4px;
        border-radius: 8px;
        background: var(--bg-subtle, #f3f4f6);
      }

      .cm-item.is-band[data-variant='danger'] {
        background: #fef3f2;
      }

      .cm-item.is-band[data-variant='primary'] {
        background: var(--h4c-bright-blue-08, rgba(39, 170, 225, 0.08));
      }

      .cm-item.is-band[data-variant='success'] {
        background: #ecfdf3;
      }

      .cm-item.is-band[data-variant='warning'] {
        background: #fef4e6;
      }

      .cm-item-check {
        flex: 0 0 auto;
        color: var(--color-success, #2bb673);
      }

      .cm-empty {
        padding: 10px;
        font-size: var(--text-sm, 0.75rem);
        color: var(--fg-4, #9ca3af);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContextMenuComponent<T = unknown> implements AfterViewInit, OnDestroy {
  readonly config = input.required<ContextMenuConfig<T>>();
  /** The row/record the menu acts on — passed to every predicate and echoed back on select. */
  readonly context = input<T | null>(null);
  /** Disables the trigger without touching the config. */
  readonly disabled = input(false);
  /** Set false when the feature owns the trigger and calls `openFor()` / `openAtEvent()`. */
  readonly showTrigger = input(true);
  /**
   * Render the feature's own template in the panel instead of `config.items`, while still using this
   * component's body portal, anchored positioning, viewport flipping and hover/close handling.
   *
   * For a panel that REVEALS a record — a spec sheet, a preview, a chart — where the items list can't carry
   * the design. The bound `context` arrives as `$implicit`:
   *
   * ```html
   * <ng-template #detail let-row><div class="my-card">{{ row.name }}</div></ng-template>
   * <app-context-menu [config]="cfg" [context]="row" [panelContent]="detail" />
   * ```
   *
   * The panel drops its own background/border/padding in this mode, so the template is the entire surface.
   * `config.items` and the menu keyboard navigation are inert while it is set.
   */
  readonly panelContent = input<TemplateRef<unknown> | null>(null);

  readonly itemSelect = output<ContextMenuSelectEvent<T>>();
  readonly openedChange = output<boolean>();

  private readonly vcr = inject(ViewContainerRef);
  private readonly zone = inject(NgZone);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly registry = inject(ContextMenuService);

  @ViewChild('panelTpl', { static: true }) private readonly panelTpl!: TemplateRef<unknown>;
  @ViewChild('triggerBtn') private readonly triggerBtn?: ElementRef<HTMLButtonElement>;

  private readonly opened = signal(false);
  readonly isOpen = this.opened.asReadonly();

  private view: EmbeddedViewRef<unknown> | null = null;
  private panelEl: HTMLElement | null = null;
  private anchorEl: HTMLElement | null = null;
  private anchorPoint: { x: number; y: number } | null = null;
  /** Listener teardowns registered while the panel is open (anchor tracking, scroll lock). */
  private teardown: (() => void)[] = [];
  /** The click that opened the menu must not also close it when the caller lets it bubble to the document. */
  private ignoreNextDocumentClick = false;
  /** Pending hover open/close; only one is ever in flight. */
  private hoverTimer: ReturnType<typeof setTimeout> | null = null;

  readonly trigger = computed<ContextMenuTriggerConfig>(() => ({
    ...DEFAULT_TRIGGER,
    ...(this.config().trigger ?? {}),
  }));
  readonly style = computed<ContextMenuStyleConfig>(() => this.config().style ?? {});
  readonly isTriggerDisabled = computed(() => this.disabled() || this.trigger().disabled === true);

  /** Items resolved against the current `context`, with dividers/headings normalised. */
  readonly entries = computed<ResolvedEntry<T>[]>(() => {
    const ctx = this.context();
    const out: ResolvedEntry<T>[] = [];
    let lastSection: string | null = null;

    for (const item of this.config().items.filter((i) => this.isVisible(i, ctx))) {
      const heading =
        item.sectionLabel && item.sectionLabel !== lastSection ? item.sectionLabel : null;
      if (item.sectionLabel) lastSection = item.sectionLabel;

      out.push({
        item,
        label: this.labelOf(item, ctx),
        hint: this.hintOf(item, ctx),
        icon: (ctx !== null && item.iconWhen?.(ctx)) || item.icon || '',
        variant: (ctx !== null && item.variantWhen?.(ctx)) || item.variant || 'default',
        disabled: this.isDisabled(item, ctx),
        // A leading divider renders as a stray line at the top once the items above it are hidden.
        separator: item.separatorBefore === true && out.length > 0,
        section: heading,
      });
    }
    return out;
  });

  readonly hasIcons = computed(() => this.entries().some((e) => !!e.icon));

  /** Style config → CSS custom properties on the panel, so a feature never has to reach into these styles. */
  readonly panelVars = computed<Record<string, string>>(() => {
    const st = this.style();
    const hover = st.variantHoverBackground ?? {};
    const vars: Record<string, string | number | undefined> = {
      '--cm-width': this.cssSize(st.width),
      '--cm-min-width': this.cssSize(st.minWidth),
      '--cm-max-height': this.cssSize(st.maxHeight),
      '--cm-z': st.zIndex,
      '--cm-bg': st.background,
      '--cm-border': st.borderColor,
      '--cm-fg': st.textColor,
      '--cm-hover-bg': hover.default ?? st.hoverBackground,
      '--cm-hover-danger': hover.danger,
      '--cm-hover-success': hover.success,
      '--cm-hover-warning': hover.warning,
      '--cm-hover-primary': hover.primary,
      '--cm-danger': st.dangerColor,
      '--cm-radius': st.radius,
      '--cm-font-size': st.fontSize,
      '--cm-shadow': st.shadow,
    };
    // Only the overridden ones — an empty var would defeat the token fallback baked into the stylesheet.
    return Object.fromEntries(
      Object.entries(vars)
        .filter(([, value]) => value !== undefined)
        .map(([name, value]): [string, string] => [name, String(value)])
    );
  });

  /** `hidden` / `visibleWhen`; a predicate with no context yet can't be evaluated, so the item is dropped. */
  private isVisible(item: ContextMenuItem<T>, ctx: T | null): boolean {
    if (item.hidden === true) return false;
    if (!item.visibleWhen) return true;
    return ctx !== null && item.visibleWhen(ctx);
  }

  private isDisabled(item: ContextMenuItem<T>, ctx: T | null): boolean {
    if (item.disabled === true) return true;
    return !!item.disabledWhen && ctx !== null && item.disabledWhen(ctx);
  }

  private labelOf(item: ContextMenuItem<T>, ctx: T | null): string {
    return (ctx !== null && item.labelWhen?.(ctx)) || item.label;
  }

  private hintOf(item: ContextMenuItem<T>, ctx: T | null): string {
    return (ctx !== null && item.hintWhen?.(ctx)) || item.hint || '';
  }

  // ── content trigger ─────────────────────────────────────────────

  readonly opensFromContent = computed(() => this.trigger().openFromContent === true);

  /**
   * The projected content acting as the trigger. Bound on the HOST rather than a wrapper so the feature can
   * lay the content out however it likes (a grid row, a card) without an element of ours in the way.
   *
   * Re-opens rather than toggles: `mount()` no-ops while the panel is up, but the same click still reaches
   * `document` where the outside-click handler closes it — so a second click would look like nothing
   * happened. Closing first makes every click open, at the new point.
   */
  @HostListener('click', ['$event'])
  onHostClick(event: MouseEvent): void {
    if (!this.opensFromContent() || this.isTriggerDisabled()) return;

    const target = event.target as HTMLElement | null;
    const ignore = this.trigger().contentIgnoreSelector ?? DEFAULT_CONTENT_IGNORE;
    const blocked = target?.closest(ignore);
    // An interactive element may opt back IN with data-cm-open — but NEVER one that is also a link.
    //
    // An element carrying both an href and data-cm-open is ambiguous by construction: it renders as a
    // link, is announced as one, and then does not navigate. That is not a preference to configure,
    // it is a contradiction, so it is refused here rather than left to each caller to get right. An
    // anchor whose click should open a panel has no business carrying an href in the first place.
    if (blocked && !this.optsIntoOpening(blocked)) return;

    // A second click on the same content CLOSES. It used to close and immediately reopen at the new
    // pointer position, which is right for a right-click menu that should follow the pointer
    // (onOwnerContextMenu still does exactly that) and wrong for a panel a row is toggling: the
    // click that was meant to dismiss it put it straight back.
    if (this.opened()) {
      this.close();
      return;
    }

    // A panel that REVEALS the row anchors to the row, so the row it describes stays visible. Opening
    // it at the pointer put its top edge wherever the click landed — inside the row — and a tall card
    // then covered the very thing it was describing. An items menu still follows the pointer: that is
    // what a context menu is.
    //
    // A keyboard activation reports no pointer position and would land the panel at 0,0 either way.
    if (this.panelContent() || event.detail === 0) this.openFor(this.host.nativeElement);
    else this.openAt(event.clientX, event.clientY);
  }

  // ── hover trigger ───────────────────────────────────────────────

  readonly opensOnHover = computed(() => this.trigger().openOn === 'hover');

  /** Click is the trigger's job only in click mode — in hover mode the pointer already opened it. */
  onTriggerClick(event: Event): void {
    if (this.opensOnHover()) {
      event.stopPropagation();
      return;
    }
    this.toggle(event);
  }

  /**
   * Open on hover, anchored to `anchor`. Public so a feature that owns its own trigger
   * (`[showTrigger]="false"`) gets the same delays and cross-onto-the-panel grace as the built-in one,
   * instead of re-inventing them — and, critically, without re-inventing the body-portalled positioning.
   */
  onHoverIn(anchor: HTMLElement): void {
    if (!this.opensOnHover()) return;
    this.cancelHoverTimer();
    if (this.opened()) return;
    this.hoverTimer = setTimeout(() => {
      this.hoverTimer = null;
      this.openFor(anchor);
    }, this.trigger().hoverOpenDelay ?? HOVER_OPEN_DELAY);
  }

  /** Pointer left the trigger OR the panel — close after a grace period so the gap between them is safe. */
  onHoverOut(): void {
    if (!this.opensOnHover()) return;
    this.cancelHoverTimer();
    this.hoverTimer = setTimeout(() => {
      this.hoverTimer = null;
      this.close();
    }, this.trigger().hoverCloseDelay ?? HOVER_CLOSE_DELAY);
  }

  /** The pointer made it onto the panel — cancel the pending close so it can be read. */
  onPanelHoverIn(): void {
    if (this.opensOnHover()) this.cancelHoverTimer();
  }

  private cancelHoverTimer(): void {
    if (this.hoverTimer === null) return;
    clearTimeout(this.hoverTimer);
    this.hoverTimer = null;
  }

  // ── open / close ────────────────────────────────────────────────
  toggle(event?: Event): void {
    event?.stopPropagation();
    if (this.opened()) {
      this.close();
      return;
    }
    const target =
      (event?.currentTarget as HTMLElement | undefined) ?? this.triggerBtn?.nativeElement;
    this.openFor(target ?? this.host.nativeElement);
  }

  /** Open anchored under an element the feature owns (a custom trigger, a grid cell button). */
  openFor(anchor: HTMLElement): void {
    this.anchorEl = anchor;
    this.anchorPoint = null;
    this.mount();
  }

  /** Open at viewport coordinates — for right-click menus. */
  openAt(x: number, y: number): void {
    this.anchorEl = null;
    this.anchorPoint = { x, y };
    this.mount();
  }

  /** Open at a right-click, suppressing the browser menu. */
  openAtEvent(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.openAt(event.clientX, event.clientY);
  }

  close(): void {
    this.cancelHoverTimer();
    if (!this.opened()) return;
    this.registry.release(this);
    for (const detach of this.teardown) detach();
    this.teardown = [];
    // The panel was moved out of the view's own DOM position, so take it out of body before destroying.
    this.panelEl?.remove();
    this.view?.destroy();
    this.view = null;
    this.panelEl = null;
    this.anchorEl = null;
    this.anchorPoint = null;
    this.opened.set(false);
    this.openedChange.emit(false);
  }

  select(entry: ResolvedEntry<T>): void {
    if (entry.disabled) return;
    if (this.config().closeOnSelect !== false) this.close();
    this.itemSelect.emit({ id: entry.item.id, item: entry.item, context: this.context() });
  }

  // ── panel plumbing ──────────────────────────────────────────────
  private mount(): void {
    if (this.opened()) return;
    // One menu at a time: closes whichever other menu is open (their triggers stop propagation, so their
    // own outside-click listener never fires for a click on this trigger).
    this.registry.present(this);

    this.view = this.vcr.createEmbeddedView(this.panelTpl);
    this.view.detectChanges();
    this.panelEl =
      this.view.rootNodes.find((n): n is HTMLElement => n instanceof HTMLElement) ?? null;
    if (this.panelEl) document.body.appendChild(this.panelEl);

    this.opened.set(true);
    this.openedChange.emit(true);
    // position() sizes the panel and then places it; both are one measurement pass, taken now that the
    // content is in the DOM and before the enter animation touches anything.
    this.position();
    // A hover-opened panel must not take focus: the pointer merely passed over something, and pulling focus
    // out of whatever the user was actually using would be hostile.
    if (!this.opensOnHover()) this.panelEl?.focus({ preventScroll: true });

    this.zone.runOutsideAngular(() => {
      this.ignoreNextDocumentClick = true;
      setTimeout(() => (this.ignoreNextDocumentClick = false));
      this.teardown.push(this.trackAnchor());
      // Hover panels default to NOT freezing the page — the user never asked for this panel, so trapping
      // their scroll would be unexplainable. A config value still wins either way.
      if (this.config().lockScroll ?? !this.opensOnHover())
        this.teardown.push(this.blockPageScroll());
    });
  }

  /**
   * Capture phase: scrolling an inner container doesn't bubble, so a bubbling listener would leave the
   * panel stranded over unrelated content. Still needed with the scroll lock on — a scrollbar drag or a
   * programmatic scroll isn't an input event and gets through.
   */
  private trackAnchor(): () => void {
    const onScroll = () => this.position();
    // Resize changes the space the height was measured against, and position() re-measures both — so
    // shrinking the window re-fits the panel rather than leaving it clamped to the old viewport.
    const onResize = () => this.position();
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onResize);
    };
  }

  /**
   * Freeze the page while the menu is open. This blocks the scroll INPUTS (wheel, touch drag, scroll keys)
   * rather than setting `overflow: hidden` on the scroll ancestors: hiding a scrollbar reflows the content
   * underneath, so the row the menu is anchored to would visibly jump as the menu opens. The panel's own
   * overflow still scrolls — a long menu has to stay usable.
   */
  private blockPageScroll(): () => void {
    const blockInput = (event: Event) => {
      if (this.isInsidePanel(event.target)) return;
      event.preventDefault();
    };
    const blockKeys = (event: KeyboardEvent) => {
      // Inside the panel these are menu navigation, handled by onKeydown(). In a field they are typing and
      // caret movement — blocking Space there would swallow the user's spaces.
      if (
        !SCROLL_KEYS.has(event.key) ||
        this.isInsidePanel(event.target) ||
        isEditable(event.target)
      )
        return;
      event.preventDefault();
    };

    // passive: false — a passive listener isn't allowed to preventDefault, which is the whole point here.
    document.addEventListener('wheel', blockInput, { capture: true, passive: false });
    document.addEventListener('touchmove', blockInput, { capture: true, passive: false });
    document.addEventListener('keydown', blockKeys, { capture: true });
    return () => {
      document.removeEventListener('wheel', blockInput, { capture: true });
      document.removeEventListener('touchmove', blockInput, { capture: true });
      document.removeEventListener('keydown', blockKeys, { capture: true });
    };
  }

  private isInsidePanel(target: EventTarget | null): boolean {
    return !!this.panelEl && target instanceof Node && this.panelEl.contains(target);
  }

  /**
   * Decide how tall the panel may be and which side of the anchor it opens on, then write the height.
   *
   * **The panel sizes to its content.** A `max-height` is written only when the content cannot fit
   * either above or below the anchor — and the space it is measured against is the VIEWPORT, because
   * the panel is `position: fixed` in `document.body`: no ancestor's box has any bearing on the room
   * it has.
   *
   * Order matters twice over. The cap is cleared BEFORE the natural height is read, so the measurement
   * is the height the content wants rather than whatever the previous open left behind; and the height
   * is written before `position()` reads the box, so the flip is computed against the real height.
   */
  private applyFit(el: HTMLElement, anchor: DOMRect | null): 'below' | 'above' {
    const st = this.style();
    const gap = (st.offset ?? 4) + VIEWPORT_MARGIN;
    const edge = anchor ?? this.pointEdge();
    const below = window.innerHeight - edge.bottom - gap;
    const above = edge.top - gap;

    // A pinned maxHeight, or a projected template that owns its own sizing: nothing to decide but
    // the side. Capping a bare wrapper would clip the feature's card (and its shadow) instead.
    if (st.maxHeight !== undefined || this.panelContent()) {
      return this.sideFor(el.offsetHeight, below, above);
    }

    el.style.removeProperty('max-height');
    const rowCap = this.rowCapHeight(el, st.maxVisibleItems);
    const natural = rowCap === null ? el.offsetHeight : Math.min(el.offsetHeight, rowCap);

    if (natural <= below || natural <= above) {
      // Fits somewhere at its natural height. The only height worth writing is a row cap a caller
      // asked for; anything else here would be a cap on a panel that does not need one.
      if (rowCap !== null && rowCap < el.offsetHeight) el.style.maxHeight = `${rowCap}px`;
      return natural <= below ? 'below' : 'above';
    }

    // Fits neither way. Take the roomier side and scroll — but never squeeze below MIN_CLAMP_HEIGHT: a
    // menu showing two and a half rows is worse than one that overhangs the edge slightly.
    el.style.maxHeight = `${Math.max(MIN_CLAMP_HEIGHT, Math.max(below, above))}px`;
    return this.roomierSide(below, above);
  }

  private sideFor(height: number, below: number, above: number): 'below' | 'above' {
    if (height <= below) return 'below';
    if (height <= above) return 'above';
    // Fits neither way — take the side with more room and let it scroll.
    return this.roomierSide(below, above);
  }

  /** Which side of the trigger has more room. Ties go below, where a menu is expected. */
  private roomierSide(below: number, above: number): 'below' | 'above' {
    return below >= above ? 'below' : 'above';
  }

  /** A right-click open has a point, not a rect — a zero-height edge at the pointer behaves as one. */
  private pointEdge(): { top: number; bottom: number } {
    const y = this.anchorPoint?.y ?? VIEWPORT_MARGIN;
    return { top: y, bottom: y };
  }

  /**
   * Height that shows exactly the first N rows, or null when no cap applies.
   *
   * **Opt-in.** This used to default to six rows, and that default is where the mystery clamp came
   * from: an eight-item menu was cut at the top of its seventh row — a fractional, content-dependent
   * number that looked like a broken viewport calculation and was nothing of the kind.
   *
   * The rows are measured rather than assumed a fixed height, so a `density` / `fontSize` override —
   * and any header, separator or section label above the cut — is accounted for.
   */
  private rowCapHeight(el: HTMLElement, limit: number | undefined): number | null {
    if (limit === undefined) return null;
    const rows = el.querySelectorAll<HTMLElement>('.cm-item');
    if (limit <= 0 || rows.length <= limit) return null;

    const box = getComputedStyle(el);
    // Cut at the top of the first row that must stay hidden; measuring from the panel's own top edge means
    // everything above that row is already counted.
    const cut = rows[limit].getBoundingClientRect().top - el.getBoundingClientRect().top;
    return cut + parseFloat(box.paddingBottom) + parseFloat(box.borderBottomWidth);
  }

  private position(): void {
    const el = this.panelEl;
    if (!el) return;

    const anchor = this.anchorEl?.getBoundingClientRect() ?? null;
    // The anchor scrolled out of the viewport — a menu floating over unrelated rows is worse than none.
    if (this.anchorEl && this.isOffViewport(anchor)) {
      this.zone.run(() => this.close());
      return;
    }

    // Height before geometry: the side the panel flips to depends on how tall it ends up being.
    const side = this.applyFit(el, anchor);

    const box = el.getBoundingClientRect();
    const coords = anchor
      ? this.coordsFromAnchor(anchor, box, side)
      : this.coordsFromPoint(box, side);
    el.style.top = `${this.clamp(coords.top, window.innerHeight - box.height)}px`;
    el.style.left = `${this.clamp(coords.left, window.innerWidth - box.width)}px`;
  }

  private isOffViewport(rect: DOMRect | null): boolean {
    return !rect || rect.bottom < 0 || rect.top > window.innerHeight;
  }

  /**
   * The side is decided by {@link applyFit}, which measured the panel at the height it will have; this
   * only turns that decision into coordinates. It used to re-derive the side here from a box whose
   * height had been capped by a rule that had nothing to do with the viewport.
   */
  private coordsFromAnchor(
    anchor: DOMRect,
    box: DOMRect,
    side: 'below' | 'above'
  ): { top: number; left: number } {
    const st = this.style();
    const placement: ContextMenuPlacement = st.placement ?? 'bottom-end';
    const offset = st.offset ?? 4;

    return {
      top: side === 'below' ? anchor.bottom + offset : anchor.top - box.height - offset,
      left: placement.endsWith('end') ? anchor.right - box.width : anchor.left,
    };
  }

  private coordsFromPoint(box: DOMRect, side: 'below' | 'above'): { top: number; left: number } {
    const offset = this.style().offset ?? 4;
    const point = this.anchorPoint ?? { x: VIEWPORT_MARGIN, y: VIEWPORT_MARGIN };
    return {
      top: side === 'below' ? point.y + offset : point.y - box.height - offset,
      left: point.x,
    };
  }

  private clamp(value: number, max: number): number {
    return Math.max(VIEWPORT_MARGIN, Math.min(value, max - VIEWPORT_MARGIN));
  }

  private cssSize(value: number | string | undefined): string | undefined {
    if (value === undefined) return undefined;
    return typeof value === 'number' ? `${value}px` : value;
  }

  // ── keyboard + outside click ────────────────────────────────────
  onKeydown(event: KeyboardEvent): void {
    const items = Array.from(
      this.panelEl?.querySelectorAll<HTMLElement>('.cm-item:not(.is-disabled)') ?? []
    );

    switch (event.key) {
      case 'Escape':
        event.stopPropagation();
        this.close();
        this.triggerBtn?.nativeElement.focus();
        break;
      case 'ArrowDown':
        this.moveFocus(event, items, 1);
        break;
      case 'ArrowUp':
        this.moveFocus(event, items, -1);
        break;
      case 'Home':
        this.focusItem(event, items, 0);
        break;
      case 'End':
        this.focusItem(event, items, items.length - 1);
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  private moveFocus(event: KeyboardEvent, items: HTMLElement[], step: 1 | -1): void {
    if (!items.length) return;
    const current = items.indexOf(document.activeElement as HTMLElement);
    const from = step === 1 ? -1 : 0;
    this.focusItem(
      event,
      items,
      ((((current === -1 ? from : current) + step) % items.length) + items.length) % items.length
    );
  }

  private focusItem(event: KeyboardEvent, items: HTMLElement[], index: number): void {
    if (!items.length) return;
    event.preventDefault();
    items[index].focus();
  }

  /** The panel stops its own clicks, so anything reaching the document is an outside click. */
  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.ignoreNextDocumentClick) {
      this.ignoreNextDocumentClick = false;
      return;
    }
    this.close();
  }

  @HostListener('document:keydown.escape')
  onDocumentEscape(): void {
    this.close();
  }

  // ── right-click on an ancestor ──────────────────────────────────

  /**
   * Install the right-click opener on the ancestor named by `trigger.contextMenuHost`.
   *
   * Wired here rather than as a host listener because the element that should own the gesture is not
   * this component — it is the row around it, whose LEFT click is already spent on something else.
   * Listening on the row is also what makes the whole row the target instead of just the kebab.
   */
  ngAfterViewInit(): void {
    const selector = this.trigger().contextMenuHost;
    if (!selector) return;

    const owner = this.host.nativeElement.closest(selector);
    if (!(owner instanceof HTMLElement)) return;

    this.contextMenuOwner = owner;
    owner.addEventListener('contextmenu', this.onOwnerContextMenu);
  }

  private contextMenuOwner: HTMLElement | null = null;

  /**
   * True for an interactive element that has asked for its click to open the panel — and is not a
   * link. See onHostClick for why those two cannot be combined.
   */
  private optsIntoOpening(el: Element): boolean {
    if (!el.hasAttribute('data-cm-open')) return false;
    return !(el instanceof HTMLAnchorElement && el.hasAttribute('href'));
  }

  private readonly onOwnerContextMenu = (event: MouseEvent): void => {
    if (this.isTriggerDisabled()) return;
    // The browser menu is what a reader gets everywhere else; here the row has its own, so it wins.
    event.preventDefault();
    event.stopPropagation();
    if (this.opened()) this.close();
    this.openAt(event.clientX, event.clientY);
  };

  ngOnDestroy(): void {
    this.contextMenuOwner?.removeEventListener('contextmenu', this.onOwnerContextMenu);
    this.close();
  }
}
